import { useMemo } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { addRendition } from '@shared/utils/photoRendition';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import type { SupportedHeaderPhotoLayoutSchema } from '@shared/types/WebsiteHeaderSettings.types';
import { getHeaderPhotoUrls } from './getHeaderPhotoUrls';

type SmartPreloadResult = Readonly<{
  criticalUrls: ReadonlyArray<string>;
  deferredUrls: ReadonlyArray<string>;
}>;

/**
 * Determines which images are critical (current route) vs deferred (background preload).
 *
 * - Welcome route: preloads website header photos (count depends on layout type).
 *   Falls back to the welcome page's own photo when no header is configured.
 * - Specific page route: preloads that page's photo only
 * - Remaining images are deferred to requestIdleCallback
 *
 * @param headerPhoto - Validated header photo schema from `useWebsiteHeaderSettings().websiteHeader?.headerPhoto`
 */
export function useSmartPreloadImageUrls(
  pages: ReadonlyArray<EventPageFragment>,
  eventHandle: string,
  headerPhoto: SupportedHeaderPhotoLayoutSchema | null | undefined,
  fallbackPhotoUrl?: string | null,
): SmartPreloadResult {
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);
  // checkIsWelcomePage is stable — useWelcomePage wraps it in useCallback
  const { checkIsWelcomePage } = useWelcomePage();

  return useMemo(() => {
    const { allUrls: headerUrls, criticalCount: headerCriticalCount } = getHeaderPhotoUrls(headerPhoto);

    // All page photo URLs at xlarge rendition, keyed by pageSlug for O(1) lookup
    const pagePhotoUrlsBySlug = new Map<string, string>();
    for (const page of pages) {
      if (page.photoV2?.url) {
        const url = addRendition({ url: page.photoV2.url, renditionSize: 'xlarge' });
        pagePhotoUrlsBySlug.set(page.pageSlug, url);
      }
    }
    const allPagePhotoUrls = Array.from(pagePhotoUrlsBySlug.values());

    // Determine if user is on the welcome page
    const currentPage = currentPageSlug ? pages.find(p => p.pageSlug === currentPageSlug) : undefined;
    const isOnWelcomePage = !currentPageSlug || (currentPage ? checkIsWelcomePage(currentPage.pageSlug, currentPage.type).isWelcomePage : true);

    let criticalUrls: string[];
    let deferredUrls: string[];

    if (isOnWelcomePage) {
      // Welcome route: preload header photos (up to critical count), defer everything else.
      criticalUrls = headerUrls.slice(0, headerCriticalCount);

      // Fallback: events with no active header (e.g. legacy Aloha header, no header configured)
      // still have an above-the-fold hero image from the welcome page's own photo.
      if (criticalUrls.length === 0) {
        const welcomePage = pages.find(p => checkIsWelcomePage(p.pageSlug, p.type).isWelcomePage);
        const welcomePhotoUrl = welcomePage?.photoV2?.url ?? fallbackPhotoUrl ?? null;
        if (welcomePhotoUrl) {
          criticalUrls = [addRendition({ url: welcomePhotoUrl, renditionSize: 'xlarge' })];
        }
      }

      const remainingHeaderUrls = headerUrls.slice(headerCriticalCount);
      deferredUrls = [...remainingHeaderUrls, ...allPagePhotoUrls];
    } else {
      // Specific page route: preload that page's photo, defer header + other page photos
      const currentPagePhotoUrl = currentPageSlug ? pagePhotoUrlsBySlug.get(currentPageSlug) : undefined;
      // Caller can provide a fallback photo (e.g. event's primary photo) for pages with no photoV2
      const fallbackUrl = fallbackPhotoUrl ? addRendition({ url: fallbackPhotoUrl, renditionSize: 'xlarge' }) : undefined;
      const photoToPreload = currentPagePhotoUrl || fallbackUrl;
      criticalUrls = photoToPreload ? [photoToPreload] : [];
      deferredUrls = [...headerUrls, ...allPagePhotoUrls];
    }

    // Deduplicate: remove from deferred anything already in critical
    const criticalSet = new Set(criticalUrls);
    deferredUrls = deferredUrls.filter(url => !criticalSet.has(url));

    return { criticalUrls, deferredUrls };
  }, [pages, currentPageSlug, headerPhoto, checkIsWelcomePage, fallbackPhotoUrl]);
}
